import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { CircleNotch, ArrowLeft, ArrowRight, FloppyDisk, Rocket, Plus, Trash } from "@phosphor-icons/react";
import CoPilotPanel, { CoPilotToggle } from "@/components/CoPilotPanel";
import ContextCarryOver from "@/components/ContextCarryOver";

const STEPS = [
  { n: 1, code: "1", label: "Concern", phase: "OPERATIONS" },
  { n: 2, code: "2", label: "Cause", phase: "OPERATIONS" },
  { n: 3, code: "3", label: "Countermeasure", phase: "OPERATIONS" },
];
const LAST = 3;
const uid = () => "cm_" + Math.random().toString(36).slice(2, 10);
const empty = { concern: "", location: "", dateShift: "", exposure: "", whys: ["", "", "", "", ""], countermeasures: [] };
const inputCls = "flex h-10 w-full rounded-sm border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const taCls = "w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const labelCls = "block text-xs font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1.5";

export default function Ccc() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [data, setData] = useState(empty);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try { const res = await api.get(`/documents/${id}`); setTitle(res.data.title || ""); setData({ ...empty, ...(res.data.payload || {}), whys: [...(res.data.payload?.whys || []), "", "", "", "", ""].slice(0, 5), countermeasures: res.data.payload?.countermeasures || [] }); }
      catch { toast.error("Document not found"); navigate("/dashboard"); }
      finally { setLoading(false); }
    })();
  }, [id]); // eslint-disable-line

  const persist = useCallback(async (extra = {}) => {
    setSaving(true);
    try { await api.put(`/documents/${id}`, { title: title || "Untitled 3C Problem Solver", payload: data, ...extra }); return true; }
    catch { toast.error("Save failed"); return false; }
    finally { setSaving(false); }
  }, [id, title, data]);

  const cur = STEPS[step - 1];
  const set = (patch) => setData((d) => ({ ...d, ...patch }));
  const setWhy = (i, v) => setData((d) => ({ ...d, whys: d.whys.map((w, j) => (j === i ? v : w)) }));
  const addCm = () => setData((d) => ({ ...d, countermeasures: [...d.countermeasures, { id: uid(), name: "", owner: "", deadline: "", status: false }] }));
  const setCm = (i, patch) => setData((d) => ({ ...d, countermeasures: d.countermeasures.map((c, j) => (j === i ? { ...c, ...patch } : c)) }));
  const removeCm = (i) => setData((d) => ({ ...d, countermeasures: d.countermeasures.filter((_, j) => j !== i) }));

  const saveDraft = async () => { if (await persist()) toast.success("Draft saved"); };
  const next = async () => { await persist(); setStep((s) => Math.min(LAST, s + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const back = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const launch = async () => {
    if (!data.countermeasures.some((c) => c.name.trim())) { toast.error("Add at least one countermeasure to track"); setStep(3); return; }
    if (await persist({ status: "Active Execution" })) { toast.success("Countermeasures launched"); navigate(`/ccc/${id}/track`); }
  };
  const applySuggestion = (text) => {
    if (step === 3) setData((d) => ({ ...d, countermeasures: [...d.countermeasures, { id: uid(), name: text, owner: "", deadline: "", status: false }] }));
    else if (step === 1) set({ concern: !data.concern?.trim() ? text : `${data.concern.trimEnd()}\n- ${text}` });
    else { const i = data.whys.findIndex((w) => !w.trim()); if (i >= 0) setWhy(i, text); }
    toast.success("Suggestion added");
  };
  const onPull = ({ title: t, context }) => { set({ concern: context || data.concern }); if (!title && t) setTitle(t); toast.success("Context pulled in"); };

  if (loading) return <Layout><div className="flex items-center justify-center py-40 text-slate-400"><CircleNotch size={28} className="animate-spin" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto w-full py-8 px-6 lg:px-10">
        <button data-testid="back-link" onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft size={16} /> Workspace</button>
        <input data-testid="ccc-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Problem title…" className="w-full bg-transparent text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 placeholder:text-slate-300 focus:outline-none border-b border-transparent focus:border-slate-300 pb-1" />
        <div className="mt-5 grid grid-cols-3 gap-2" data-testid="ccc-stepper">
          {STEPS.map((s) => (
            <button key={s.n} data-testid={`step-${s.n}`} onClick={() => setStep(s.n)} className={`rounded-sm border px-3 py-2.5 text-center transition-all ${step === s.n ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <span className={`font-mono text-xs font-bold block ${step === s.n ? "text-blue-700" : "text-slate-400"}`}>{s.label[0]}</span>
              <p className={`text-xs font-medium ${step === s.n ? "text-slate-900" : "text-slate-500"}`}>{s.label}</p>
            </button>
          ))}
        </div>
        <div className="mt-6 bg-white border border-slate-200 rounded-sm shadow-sm p-6 space-y-4">
          {step === 1 && (
            <>
              <h2 className="text-xl font-semibold text-slate-900">Concern</h2>
              <ContextCarryOver currentId={id} onPull={onPull} />
              <div><label className={labelCls}>Problem Statement</label><textarea data-testid="field-concern" rows={4} className={taCls} value={data.concern} onChange={(e) => set({ concern: e.target.value })} placeholder="The raw operational problem…" /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className={labelCls}>Location</label><input data-testid="field-location" className={inputCls} value={data.location} onChange={(e) => set({ location: e.target.value })} placeholder="Line / area" /></div>
                <div><label className={labelCls}>Date / Shift</label><input data-testid="field-dateShift" className={inputCls} value={data.dateShift} onChange={(e) => set({ dateShift: e.target.value })} placeholder="When it occurred" /></div>
              </div>
              <div><label className={labelCls}>Immediate Exposure / Risk</label><input data-testid="field-exposure" className={inputCls} value={data.exposure} onChange={(e) => set({ exposure: e.target.value })} placeholder="Safety / cost / quality exposure" /></div>
            </>
          )}
          {step === 2 && (
            <>
              <h2 className="text-xl font-semibold text-slate-900">Cause — 5 Whys</h2>
              <p className="text-sm text-slate-500">Step past symptoms down to the structural root failure.</p>
              <div className="space-y-2">
                {data.whys.map((w, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="font-mono text-xs text-amber-700 w-12 shrink-0">WHY {i + 1}</span>
                    <input data-testid={`field-why-${i}`} className={inputCls + " flex-1"} value={w} onChange={(e) => setWhy(i, e.target.value)} placeholder={i === 0 ? "Why did the concern occur?" : "…and why is that?"} />
                  </div>
                ))}
              </div>
            </>
          )}
          {step === 3 && (
            <>
              <div className="flex items-center justify-between"><h2 className="text-xl font-semibold text-slate-900">Countermeasures</h2><button data-testid="ccc-add-cm" onClick={addCm} className="inline-flex items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"><Plus size={16} /> Add</button></div>
              <p className="text-sm text-slate-500">Corrective tasks with owners and deadlines — these become your live checklist.</p>
              <div className="space-y-2">
                {data.countermeasures.map((c, i) => (
                  <div key={c.id} data-testid={`cm-row-${i}`} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center border border-slate-200 rounded-sm p-2">
                    <input data-testid={`cm-name-${i}`} className={inputCls + " md:col-span-6"} value={c.name} onChange={(e) => setCm(i, { name: e.target.value })} placeholder="Corrective action" />
                    <input data-testid={`cm-owner-${i}`} className={inputCls + " md:col-span-3"} value={c.owner} onChange={(e) => setCm(i, { owner: e.target.value })} placeholder="Owner" />
                    <input data-testid={`cm-deadline-${i}`} type="date" className={inputCls + " md:col-span-2"} value={c.deadline} onChange={(e) => setCm(i, { deadline: e.target.value })} />
                    <button data-testid={`cm-remove-${i}`} onClick={() => removeCm(i)} className="md:col-span-1 p-2 text-slate-400 hover:text-red-600 justify-self-end"><Trash size={16} /></button>
                  </div>
                ))}
                {data.countermeasures.length === 0 && <p className="text-sm text-slate-400 border border-dashed border-slate-300 rounded-sm p-4 text-center">No countermeasures yet.</p>}
              </div>
            </>
          )}
        </div>
        <div className="mt-6 flex items-center justify-between gap-3 sticky bottom-0 bg-slate-50 py-4">
          <button data-testid="ccc-back" onClick={back} disabled={step === 1} className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"><ArrowLeft size={16} /> Back</button>
          <div className="flex items-center gap-3">
            <button data-testid="ccc-save-draft" onClick={saveDraft} disabled={saving} className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">{saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} />} Save Draft</button>
            {step < LAST ? (
              <button data-testid="ccc-next" onClick={next} className="inline-flex items-center gap-2 rounded-sm bg-blue-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-800">Next Step <ArrowRight size={16} /></button>
            ) : (
              <button data-testid="ccc-launch" onClick={launch} disabled={saving} className="inline-flex items-center gap-2 rounded-sm bg-blue-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-800"><Rocket size={18} weight="fill" /> Launch Tracking</button>
            )}
          </div>
        </div>
      </div>
      <CoPilotToggle open={open} onClick={() => setOpen(true)} phase={cur.phase} />
      <CoPilotPanel open={open} onClose={() => setOpen(false)} docId={id} toolType="ccc" step={cur} phase={cur.phase} onApplySuggestion={applySuggestion} />
    </Layout>
  );
}
